import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  ChefHat,
  Flame,
  Loader2,
  LogOut,
  Pizza,
  Sandwich,
  Soup,
  UtensilsCrossed,
} from 'lucide-react';
import { API_BASE } from '../apiBase';
import { clearSession } from '../auth';

const cx = (...parts) => parts.filter(Boolean).join(' ');

const STATION_THEMES = [
  {
    Icon: ChefHat,
    iconBg: 'bg-gradient-to-br from-emerald-400 to-green-600',
    hover: 'hover:border-emerald-300 hover:shadow-[0_8px_24px_rgba(16,185,129,0.18)]',
  },
  {
    Icon: Flame,
    iconBg: 'bg-gradient-to-br from-orange-400 to-amber-600',
    hover: 'hover:border-orange-300 hover:shadow-[0_8px_24px_rgba(249,115,22,0.18)]',
  },
  {
    Icon: Pizza,
    iconBg: 'bg-gradient-to-br from-rose-400 to-red-500',
    hover: 'hover:border-rose-300 hover:shadow-[0_8px_24px_rgba(244,63,94,0.18)]',
  },
  {
    Icon: Sandwich,
    iconBg: 'bg-gradient-to-br from-sky-400 to-blue-600',
    hover: 'hover:border-sky-300 hover:shadow-[0_8px_24px_rgba(14,165,233,0.18)]',
  },
  {
    Icon: Soup,
    iconBg: 'bg-gradient-to-br from-violet-400 to-purple-600',
    hover: 'hover:border-violet-300 hover:shadow-[0_8px_24px_rgba(139,92,246,0.18)]',
  },
  {
    Icon: UtensilsCrossed,
    iconBg: 'bg-gradient-to-br from-teal-400 to-cyan-600',
    hover: 'hover:border-teal-300 hover:shadow-[0_8px_24px_rgba(20,184,166,0.18)]',
  },
];

const MenuSelection = () => {
  const navigate = useNavigate();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchMenus = async () => {
      try {
        const id_caisse = localStorage.getItem('id_caisse');
        const id_account_caisse = localStorage.getItem('id_account_caisse');
        const response = await axios.get(`${API_BASE}/menus/${id_caisse}`, {
          params: { id_account_caisse },
        });
        setMenus(response.data);
        setLoading(false);
      } catch (err) {
        setError('Impossible de charger les points de préparation.');
        setLoading(false);
      }
    };
    fetchMenus();
  }, []);

  const handleMenuSelect = (menuName) => {
    navigate(`/kitchen-display/${encodeURIComponent(menuName)}`);
  };

  const handleLogout = () => {
    clearSession();
    navigate('/');
  };

  return (
    <div className="flex min-h-screen flex-col bg-kd-wash font-sans text-kd-ink">
      <header className="sticky top-0 z-[100] border-b border-kd-line/80 bg-white/90 shadow-header backdrop-blur-xl">
        <div className="flex items-center justify-between gap-3 px-3 py-2.5 sm:px-4">
          <div className="flex min-w-0 items-center gap-3">
            <img
              src="/lacaisse_logo.svg"
              alt="La Caisse"
              className="hidden h-auto w-[200px] shrink-0 sm:block lg:w-[230px]"
            />
            <button
              type="button"
              className="kd-icon-btn border-kd-line bg-kd-wash text-kd-ink hover:bg-white"
              onClick={() => navigate('/dashboard')}
              title="Retour au tableau de bord"
              aria-label="Retour au tableau de bord"
            >
              <ArrowLeft aria-hidden="true" />
              <span className="hidden sm:inline">Retour</span>
            </button>
          </div>

          <button
            type="button"
            className="kd-icon-btn border-slate-800 bg-slate-800 text-white hover:border-slate-900 hover:bg-slate-900"
            onClick={handleLogout}
            title="Déconnexion"
            aria-label="Déconnexion"
          >
            <LogOut aria-hidden="true" />
            <span className="hidden sm:inline">Quitter</span>
          </button>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-6xl flex-1 flex-col items-center px-3 py-6 sm:px-4 sm:py-8">
        {loading ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-16">
            <Loader2 className="h-10 w-10 animate-kd-spin text-kd-brand" aria-hidden="true" />
            <p className="text-sm font-semibold text-kd-soft">Chargement des stations…</p>
          </div>
        ) : error ? (
          <div className="max-w-md rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-center text-sm font-semibold text-kd-danger">
            {error}
          </div>
        ) : menus.length === 0 ? (
          <div className="max-w-md rounded-2xl border border-kd-line bg-white px-4 py-8 text-center shadow-sm">
            <UtensilsCrossed className="mx-auto mb-3 h-10 w-10 text-kd-muted" strokeWidth={1.75} aria-hidden="true" />
            <p className="font-semibold text-kd-ink">Aucun point de préparation disponible</p>
            <p className="mt-1 text-sm text-kd-soft">Vérifiez la configuration des imprimantes dans La Caisse.</p>
          </div>
        ) : (
          <div className="flex w-full max-w-4xl flex-col items-center">
            <div className="mb-6 text-center sm:mb-8">
              <div className="mx-auto mb-2 inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-kd-soft ring-1 ring-kd-line">
                <ChefHat className="h-3 w-3 text-kd-brand" strokeWidth={2.25} aria-hidden="true" />
                Présentoir cuisine
              </div>
              <h1 className="text-xl font-extrabold tracking-tight text-kd-ink sm:text-2xl">
                Point de préparation
              </h1>
              <p className="mx-auto mt-1.5 max-w-md text-xs font-medium text-kd-soft sm:text-sm">
                Touchez une station pour ouvrir son écran.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-3 pb-6 sm:gap-4">
              {menus.map((menu, index) => {
                const theme = STATION_THEMES[index % STATION_THEMES.length];
                const { Icon } = theme;
                return (
                  <button
                    key={menu.id_imprimante}
                    type="button"
                    onClick={() => handleMenuSelect(menu.nom_imprimante)}
                    className={cx(
                      'group flex w-[132px] flex-col items-center gap-3 rounded-2xl border border-white/80 bg-white/90 px-3 py-4 text-center shadow-sm backdrop-blur-sm transition duration-200 sm:w-[148px]',
                      'hover:-translate-y-1 active:scale-[0.98]',
                      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kd-brand',
                      theme.hover
                    )}
                  >
                    <span
                      className={cx(
                        'inline-flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-md transition group-hover:scale-105',
                        theme.iconBg
                      )}
                    >
                      <Icon className="h-6 w-6" strokeWidth={2.25} aria-hidden="true" />
                    </span>
                    <span className="line-clamp-2 min-h-[2.5rem] w-full text-xs font-bold leading-snug text-kd-ink sm:text-[13px]">
                      {menu.nom_imprimante}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MenuSelection;
